const CLIENT_DEFAULT = 0;

/** PRODUCT_STATUS
 * @readonly
 * @description 商品狀態(對應商品頁 API get products API 狀態)
 */
export const PRODUCT_STATUS = Object.freeze({
  // 已上架
  STATUS_LAUNCHED: 1,
  // 已下架
  STATUS_DISCONTINUED: 2,
  // 已售完
  STATUS_SOLD_OUT: 3,
  // 販賣時間已過
  STATUS_SALE_TIME_OUT: 4,
  // 尚未開賣
  STATUS_COMING_SOON: 5
});

/** PRODUCT_TYPE
 * @readonly
 * @enum {number} 商品「種類」代碼
 * @description
 * - 1: 行程類
 * - 2: 物品類
 * - 3: 票券
 * - 4: 租車
 * - 6: SIM卡/WI-FI分享器
 */
export const PRODUCT_TYPE = Object.freeze({
  // 行程類
  LOCALTOURS: 1,
  // 物品類
  GOODS: 2,
  // 票券
  TICKET: 3,
  CAR_RENTAL: 4,
  SIM_WIFI: 6,
  AGENT: 7
});

/** PRODUCT_TYPE_TABLE
 * @readonly
 * @enum {number} 商品「種類」代碼
 * @description PRODUCT_TYPE 的 hash table，目前僅有 GA 會用到
 */
export const PRODUCT_TYPE_TABLE = Object.freeze({
  [PRODUCT_TYPE.LOCALTOURS]: 'LocalTour',
  [PRODUCT_TYPE.GOODS]: 'Good',
  [PRODUCT_TYPE.TICKET]: 'Ticket',
  [PRODUCT_TYPE.SIM_WIFI]: 'SimWifi',
  [PRODUCT_TYPE.CAR_RENTAL]: 'CarRental',
  [PRODUCT_TYPE.AGENT]: 'Agent'
});

/** PRODUCT_TYPE_MAP
 * @readonly
 * @enum {number} 商品「種類」代碼
 * @summary 同樣是 PRODUCT_TYPE 的 hash table/ map，但用在 product v2 SSR proxyRouter
 * - 1: 行程類
 * - 2: 物品類
 * - 3: 票券
 * - 4: 租車
 * - 6: SIM卡/WI-FI分享器
 */
export const PRODUCT_TYPE_MAP = Object.freeze({
  1: 'localtour',
  2: 'items',
  3: 'ticket',
  4: 'car_rental',
  6: 'sim',
  7: 'agent'
});

/** CHECK_STATUS
 * @description GET /items、POST /check、POST /pay API 會用到的狀態碼，後端定義為 PAY_STATUS 但因前端檢查與付款都打，所以 Web 這裏改取為 CHECK_STATUS
 * @see http://flredmine.herokuapp.com/issues/1075
 */
const CHECK_STATUS = {
  // 成功
  SUCCESS: 1,
  // 已終止、已下架
  DISCONTINUED: 201,
  // 已售完
  SOLD_OUT: 202,
  // 庫存不足
  STOCK_INSUFFICIENT: 203,
  // 銷售超時
  SALE_TIME_OUT: 204,
  // 即將開賣
  COMING_SOON: 205,
  // 零元商品
  PAY_FREE: 206,
  // 交易金額不符
  SUBTOTAL_MISMATCH: 208,
  // 失敗
  FAIL: 299,
  // 前端自訂 pay status 只要刷卡後端沒過就用這個
  CARD_FAIL: 999
};

/** PAYMENT_STATUS
 * @description pay API 回的付款狀態碼
 * @description 基本上只要後端不是回 1 前端文字都是丟授權失敗
 * @see http://flredmine.herokuapp.com/issues/1075
 */
const PAYMENT_STATUS = {
  // 交易成功
  STATUS_SUCCESS: 1,
  // 未知錯誤
  STATUS_UNKNOWN_ERROR: 2,
  // 卡片錯誤，請洽詢發卡行
  STATUS_CARD_ERROR: 3,
  // 銀行系統逾時
  STATUS_BANK_SYSTEM_ERROR: 4,
  // 重複交易
  STATUS_DUPLICATE_TRANSACTION: 5,
  // 商家帳戶錯誤
  STATUS_BANK_MERCHANT_ACCOUNT_DATA_ERROR: 6,
  // 金額錯誤
  STATUS_AMOUNT_ERROR: 7,
  // Bank transaction id 重複
  STATUS_ORDER_NUMBER_DUPLICATE: 8,
  // 銀行錯誤
  STATUS_BANK_ERROR: 9,
  // 紅利折抵失敗
  STATUS_REDEEM_FAILED: 10,
  // 退款中
  STATUS_REFUNDING: 11,
  // 已退款
  STATUS_REFUNDED: 12,
  // 付款中
  STATUS_PAYING: 13,
  // 請款成功
  STATUS_CAPTURE_SUCCESS: 14,
  // 請款失敗
  STATUS_CAPTURE_FAIL: 15
};

// 1有場次、2無場次
const SESSION_TYPE = {
  HAS: 1,
  HAS_NOT: 2
};

/** ORDER_STATUS
 * @description 訂單狀態
 * @see http://flredmine.herokuapp.com/projects/funliday/wiki/Order_status_%E5%88%97%E8%A1%A8
 */
const ORDER_STATUS = {
  // 訂單已在 Funliday 上付款確認，前端顯示：Booking pending
  CONFIRMED: 1,
  // 訂單已被供應商確認，前端顯示：Booking confirmed
  PROVIDER_FINISHED: 2,
  // 訂單已出團、已出貨，前端顯示：Booking confirmed
  SHIPPED: 3,
  // local tour 已結束、貨品已收到，前端顯示：Booking confirmed
  FINISHED: 4,
  // 訂單取消中，前端顯示：Booking cancelled
  CANCELLING: 5,
  // 訂單已取消，前端顯示：Booking cancelled
  CANCELLED: 6,
  // 訂單付款失敗，前端顯示：Booking cancelled
  PAYMENT_FAIL: 7
};

/** ADD_TYPE_TO_TRIP
 * @description add product or order to trip
 * @see http://flredmine.herokuapp.com/projects/funliday/wiki/Order_status_%E5%88%97%E8%A1%A8
 */
export const ADD_TYPE_TO_TRIP = {
  ORDER: 1,
  PRODUCT: 2
};

export const EC_ENUM = {
  STATUS: PRODUCT_STATUS,
  TYPE: PRODUCT_TYPE,
  CHECK: CHECK_STATUS,
  SESSION: SESSION_TYPE,
  ORDER: ORDER_STATUS,
  PAYMENT: PAYMENT_STATUS,
  TEXT: {
    STATUS: {
      [CLIENT_DEFAULT]: 'Coming soon',
      [PRODUCT_STATUS.STATUS_LAUNCHED]: 'Book Now',
      [PRODUCT_STATUS.STATUS_DISCONTINUED]: 'No longer sold',
      [PRODUCT_STATUS.STATUS_SOLD_OUT]: 'Oops...This product is sold out',
      [PRODUCT_STATUS.STATUS_SALE_TIME_OUT]: 'Currently unavailable',
      [PRODUCT_STATUS.STATUS_COMING_SOON]: 'Coming soon'
    },
    CHECK: {
      [CLIENT_DEFAULT]: 'Coming soon',
      [CHECK_STATUS.DISCONTINUED]: 'Oops...This product is no longer sold',
      [CHECK_STATUS.SOLD_OUT]: 'Oops...This product is sold out',
      [CHECK_STATUS.STOCK_INSUFFICIENT]: 'Oops...Inventory shortage',
      [CHECK_STATUS.SALE_TIME_OUT]: 'Currently unavailable',
      [CHECK_STATUS.COMING_SOON]: 'Coming soon',
      [CHECK_STATUS.FAIL]: 'Oops! Connection error...',
      [CHECK_STATUS.CARD_FAIL]: 'Authorization failed, transaction cancelled'
    },
    ORDER: {
      [ORDER_STATUS.CONFIRMED]: 'Booking Pending',
      [ORDER_STATUS.PROVIDER_FINISHED]: 'Booking Confirmed',
      [ORDER_STATUS.SHIPPED]: 'Booking Confirmed',
      [ORDER_STATUS.FINISHED]: 'Booking Confirmed',
      [ORDER_STATUS.CANCELLING]: 'Booking Cancelled',
      [ORDER_STATUS.CANCELLED]: 'Booking Cancelled',
      [ORDER_STATUS.PAYMENT_FAIL]: 'Booking Cancelled'
    },

    PAYMENT: {
      [PAYMENT_STATUS.STATUS_SUCCESS]: 'Paid',
      [PAYMENT_STATUS.STATUS_UNKNOWN_ERROR]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_CARD_ERROR]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_BANK_SYSTEM_ERROR]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_DUPLICATE_TRANSACTION]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_BANK_MERCHANT_ACCOUNT_DATA_ERROR]:
        'Payment Failed',
      [PAYMENT_STATUS.STATUS_AMOUNT_ERROR]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_ORDER_NUMBER_DUPLICATE]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_BANK_ERROR]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_REDEEM_FAILED]: 'Payment Failed',
      [PAYMENT_STATUS.STATUS_REFUNDING]: 'Refunding',
      [PAYMENT_STATUS.STATUS_REFUNDED]: 'Refunded',
      [PAYMENT_STATUS.STATUS_PAYING]: 'Payment processing',
      [PAYMENT_STATUS.STATUS_CAPTURE_SUCCESS]: 'Paid',
      [PAYMENT_STATUS.STATUS_CAPTURE_FAIL]: 'Payment Failed'
    }
  }
};

/** CARD_TYPE
 * @description tappay 與我們後端的信用卡對應 type
 */
export const CARD_TYPE = {
  UN_KNOWN: -1,
  VISA: 1,
  MASTER_CARD: 2,
  JCB: 3,
  UNION_PAY: 4,
  AMEX: 5
};

/** CARD_TYPE
 * @description 對應信用卡 type 的選擇器樣式
 */
export const CARD_ICON = {
  [CARD_TYPE.UN_KNOWN]: 'creditcard-icon',
  [CARD_TYPE.VISA]: 'visa-icon',
  [CARD_TYPE.MASTER_CARD]: 'mastercard-icon',
  [CARD_TYPE.JCB]: 'jcb-icon',
  [CARD_TYPE.UNION_PAY]: 'unionpay-icon',
  [CARD_TYPE.AMEX]: 'americanexpress-icon'
};

/** TAPPAY_STATUS
 * @description TAPPAY <iframe> input 之狀態碼數值: 0完成 | 1空欄位 | 2欄位錯誤 | 3輸入中
 */
export const TAPPAY_STATUS = {
  complete: 0,
  empty: 1,
  error: 2,
  input: 3
};

/** LOCK_CREDIT_SECOND
 *  @description 檢查是否鎖住 30 秒用
 */
export const LOCK_CREDIT_SECOND = 30;

export const CART_TYPE = Object.freeze({
  PRODUCT: 1,
  DISCOVER: 2
});
